import React, { useEffect, useState } from "react";
import { Plus, Trash2, RotateCw, Settings2 } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { api, cachedGet, formatApiError } from "../lib/api";
import StageConfigDialog from "../components/StageConfigDialog";

const _invalidateMasterCache = () => cachedGet.invalidate((k) => k.startsWith("/masters"));

const MASTER_TYPES = [
  { key: "category", label: "Categories" },
  { key: "client_type", label: "Client Types" },
  { key: "lead_source", label: "Lead Sources" },
  { key: "sales_poc", label: "Pipelines" },
  { key: "opp_type", label: "Opportunity Types" },
  { key: "state", label: "States" },
  { key: "tagging", label: "Stage Tagging" },
  { key: "lost_reason", label: "Lost / Not-Int Reasons" },
  { key: "wh_partner", label: "WH Partners" },
  { key: "lm_partner", label: "LM Partners" },
  { key: "wms_oms", label: "WMS / OMS" },
  { key: "stage", label: "Stages" },
  { key: "utm_source", label: "UTM — Source" },
  { key: "utm_medium", label: "UTM — Medium" },
  { key: "utm_campaign", label: "UTM — Campaign" },
  { key: "utm_content", label: "UTM — Content" },
  { key: "utm_term", label: "UTM — Term" },
];

export default function AdminMasters() {
  const [type, setType] = useState("category");
  const [items, setItems] = useState([]);
  const [newVal, setNewVal] = useState("");
  const [configStage, setConfigStage] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/masters?type=${type}`);
    setItems(data);
  };

  useEffect(() => { load();    }, [type]);

  const add = async (e) => {
    e.preventDefault();
    if (!newVal.trim()) return;
    try {
      await api.post("/masters", { type, value: newVal.trim(), sort_order: items.length });
      _invalidateMasterCache();
      toast.success("Added");
      setNewVal("");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  const toggle = async (m) => {
    try {
      await api.patch(`/masters/${m.id}`, { active: !m.active });
      _invalidateMasterCache();
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  const remove = async (m) => {
    try {
      await api.delete(`/masters/${m.id}`);
      _invalidateMasterCache();
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  return (
    <Layout title="Master Data" breadcrumb={["Admin", "Masters"]}>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
        <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm h-fit">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 px-2 py-1.5">Master type</div>
          <ul className="mt-1 space-y-0.5">
            {MASTER_TYPES.map((t) => (
              <li key={t.key}>
                <button
                  data-testid={`master-type-${t.key}`}
                  onClick={() => setType(t.key)}
                  className={`w-full text-left text-sm px-3 py-2 rounded-md ${
                    type === t.key ? "bg-orange-500 text-white font-medium" : "hover:bg-slate-50 text-slate-700"
                  }`}
                >
                  {t.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-3 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex flex-wrap items-center gap-3 justify-between">
            <h3 className="text-base font-semibold text-slate-900">{MASTER_TYPES.find((t) => t.key === type)?.label}</h3>
            <form onSubmit={add} className="flex items-center gap-2">
              <input
                data-testid="master-new-value"
                value={newVal}
                onChange={(e) => setNewVal(e.target.value)}
                placeholder="Add new value…"
                className="text-sm px-3 py-1.5 rounded-md border border-slate-200 w-56"
              />
              <button data-testid="master-add-btn" className="px-3 py-1.5 rounded-md bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium inline-flex items-center gap-1"><Plus size={14} /> Add</button>
            </form>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>{["Value", "Sort", "Active", ""].map((h) => <th key={h} className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>)}</tr>
            </thead>
            <tbody>
              {items.map((m) => (
                <tr key={m.id} className="border-t border-slate-100" data-testid={`master-row-${m.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-800">{m.value}</td>
                  <td className="px-4 py-3 text-slate-500">{m.sort_order}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggle(m)} className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${m.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                      {m.active ? "Active" : "Inactive"}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {type === "stage" && (
                      <button
                        data-testid={`master-configure-${m.id}`}
                        onClick={() => setConfigStage(m)}
                        title="Configure mandatory fields, tagging & pickup rule"
                        className="text-slate-500 hover:text-orange-600 p-1.5 rounded-md hover:bg-orange-50 mr-1 inline-flex"
                      >
                        <Settings2 size={14} />
                      </button>
                    )}
                    <button onClick={() => remove(m)} className="text-slate-400 hover:text-red-500 p-1.5 rounded-md hover:bg-red-50"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-slate-500">No entries</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <StageConfigDialog
        open={!!configStage}
        stage={configStage}
        onClose={() => setConfigStage(null)}
        onSaved={load}
      />
    </Layout>
  );
}
